import { Component } from '@theme/component';

/**
 * @typedef {object} HeaderSubmenuRefs
 * @property {HTMLButtonElement[]} navTriggers - Navigation links that trigger submenus.
 */

/**
 * A custom element that manages header submenu dialogs with hover-first desktop
 * interaction and click fallback on touch/no-hover devices.
 *
 * @extends Component<HeaderSubmenuRefs>
 */
class HeaderSubmenu extends Component {
  /** @type {number | null} */
  openTimeout = null;

  /** @type {number | null} */
  closeTimeout = null;

  /** @type {HTMLDialogElement | null} */
  activeDialog = null;

  /** @type {HTMLButtonElement | null} */
  activeTrigger = null;

  /** @type {boolean} - Prevents hover from reopening after a click close */
  clickClosed = false;

  /** @type {boolean} */
  clickInteractionMode = false;

  /** @type {Array<{ element: EventTarget; type: string; handler: EventListener }>} */
  cleanupListeners = [];

  connectedCallback() {
    super.connectedCallback();

    const { navTriggers } = this.refs;
    if (!navTriggers || !Array.isArray(navTriggers) || navTriggers.length === 0) return;

    this.clickInteractionMode = this.#shouldUseClickInteractionMode();

    for (const trigger of navTriggers) {
      const targetId = trigger.getAttribute('data-submenu-target');
      if (!targetId) continue;

      const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(targetId));
      if (!dialog) continue;

      if (this.clickInteractionMode) {
        this.#addCleanupListener(trigger, 'click', (event) => this.#handleTriggerClick(event, trigger, dialog));
      } else {
        this.#addCleanupListener(trigger, 'mouseenter', () => this.#handleTriggerEnter(trigger, dialog));
        this.#addCleanupListener(trigger, 'mouseleave', () => this.#handleTriggerLeave(dialog));
        this.#addCleanupListener(dialog, 'mouseenter', () => this.#handleDialogEnter());
        this.#addCleanupListener(dialog, 'mouseleave', () => this.#handleDialogLeave());
      }
    }

    this.#addCleanupListener(document, 'keydown', (/** @type {KeyboardEvent} */ event) => {
      if (event.key === 'Escape' && this.activeDialog) {
        this.#closeDialog();
        this.activeTrigger?.focus();
      }
    });

    this.#addCleanupListener(document, 'click', (event) => {
      if (!this.activeDialog) return;

      const target = event.target;
      if (!(target instanceof Node)) return;

      const isInsideDialog = this.activeDialog.contains(target);
      const isInsideTrigger = this.activeTrigger?.contains(target);

      if (!isInsideDialog && !isInsideTrigger) {
        this.#closeDialog();
      }
    });

    this.#addCleanupListener(document, 'scroll', () => {
      if (this.activeDialog) {
        this.#closeDialog();
        this.clickClosed = false;
      }
    });
  }

  disconnectedCallback() {
    this.#clearTimeouts();
    this.#closeDialog();

    for (const { element, type, handler } of this.cleanupListeners) {
      element.removeEventListener(type, handler);
    }

    this.cleanupListeners = [];
    super.disconnectedCallback();
  }

  /**
   * @param {HTMLButtonElement} trigger
   * @param {HTMLDialogElement} dialog
   */
  #handleTriggerEnter(trigger, dialog) {
    this.#clearTimeouts();

    // Don't reopen if we just closed via click
    if (this.clickClosed) return;

    // Already open on this dialog, nothing to do
    if (this.activeDialog === dialog) return;

    this.openTimeout = window.setTimeout(() => {
      this.#openDialog(trigger, dialog);
    }, 100);
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  #handleTriggerLeave(dialog) {
    this.#clearTimeouts();

    // Reset clickClosed only when actually leaving the trigger area
    // and the dialog we're leaving is not the active one or no dialog is active
    if (this.activeDialog !== dialog) {
      this.clickClosed = false;
    }

    this.closeTimeout = window.setTimeout(() => {
      this.#closeDialog();
      this.clickClosed = false;
    }, 200);
  }

  /**
   * @param {Event} event
   * @param {HTMLButtonElement} trigger
   * @param {HTMLDialogElement} dialog
   */
  #handleTriggerClick(event, trigger, dialog) {
    if (!this.clickInteractionMode) return;

    event.preventDefault();
    event.stopPropagation();
    this.#clearTimeouts();

    if (this.activeDialog === dialog) {
      // Closing via click - set flag to prevent immediate reopen from hover
      this.clickClosed = true;
      this.#closeDialog();
    } else {
      this.clickClosed = false;
      this.#openDialog(trigger, dialog);
    }
  }

  #handleDialogEnter() {
    this.#clearTimeouts();
  }

  #handleDialogLeave() {
    this.#clearTimeouts();

    this.closeTimeout = window.setTimeout(() => {
      this.#closeDialog();
      this.clickClosed = false;
    }, 200);
  }

  /**
   * @param {HTMLButtonElement} trigger
   * @param {HTMLDialogElement} dialog
   */
  #openDialog(trigger, dialog) {
    if (this.activeDialog && this.activeDialog !== dialog) {
      this.#closeDialog();
    }

    this.activeDialog = dialog;
    this.activeTrigger = trigger;

    trigger.setAttribute('aria-expanded', 'true');
    dialog.show();
  }

  #closeDialog() {
    if (!this.activeDialog) return;

    if (this.activeTrigger) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
    }

    this.activeDialog.close();
    this.activeDialog = null;
    this.activeTrigger = null;
  }

  #clearTimeouts() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  /**
   * Touchscreen and/or no-hover devices should use click interaction.
   * @returns {boolean}
   */
  #shouldUseClickInteractionMode() {
    const hasHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const hasTouch = navigator.maxTouchPoints > 0;
    return hasTouch || !hasHover;
  }

  /**
   * @param {EventTarget} element
   * @param {string} type
   * @param {EventListener} handler
   */
  #addCleanupListener(element, type, handler) {
    element.addEventListener(type, handler);
    this.cleanupListeners.push({ element, type, handler });
  }
}

customElements.define('header-submenu', HeaderSubmenu);
